import { chatStore$ } from "@/lib/ContactsMap"
import { cyrb64Hash } from "@/lib/crypto.utils"
import { ContactSchema, type TContact } from "@/schemas/contacts"
import { dynamoStore$ } from "@/stores/dynamo.v2.store"
import { fetchMapKeyData } from "@/sync/sync"
import type { IRegisterMapProps } from "@/sync/syncTypes"
import { batch, syncState, when } from "@legendapp/state"
import consola from "consola"

function fixContactObject(data: any) {
  // Try to get the id from the data
  const contactPersonId = data?.id ? `${data?.id}` : undefined

  const contactPrevData: any | undefined = contactPersonId ? dynamoStore$.recentUserContacts.dataMap[contactPersonId].get() : {}

  return {
    ...contactPrevData,
    ContactPhoneNumber: data?.phoneNumber ?? contactPrevData?.ContactPhoneNumber,
    ContactEmail: data?.email ?? contactPrevData?.ContactEmail,
    ContactPersonId: (contactPersonId ?? data?.id) ? `${data.id}` : undefined,
    Note: data?.note ?? data?.lastNote?.data,
    ContactRemoteJson: data?.remote ? JSON.stringify(data.remote) : contactPrevData?.ContactRemoteJson,
    SearchTerm: data?.searchTerm ?? "",
    TTL: data?.ttl ?? 0,
    ContactEmails: data?.emails ?? contactPrevData?.ContactEmails ?? [],
    ContactLastDisposition: data?.lastDisposition ?? contactPrevData?.ContactLastDisposition,
    LastTimestamp: Date.now(),
    ContactPhoneNumbers: data?.phoneNumbers ?? contactPrevData?.ContactPhoneNumbers ?? [],
    ContactCompanyName: data?.company ?? contactPrevData?.ContactCompanyName ?? "",
    AccountId: data?.accountId ?? contactPrevData?.AccountId ?? "",
    ContactDNC: data?.dnc ?? false,
    ContactFullname: data?.fullName ?? data?.name ?? contactPrevData?.ContactFullname ?? "",
    Occurrences: data?.occurrences ?? contactPrevData?.Occurrences ?? 0,
  } satisfies TContact
}

export const ContactsMapHandler = (name: string): IRegisterMapProps => ({
  name,
  onFailure: (error: unknown) => {
    consola.error(`Map ${name} failed`, error)
  },
  onRemoved: () => {
    consola.debug(`Map removed: ${name}`)
  },
  onItemAdded: (data: unknown, rawData: unknown) => {
    consola.debug(`Item added to ${name}`, data, rawData)
  },
  onItemRemoved: (data: unknown, rawData: unknown) => {
    consola.debug(`Item removed from ${name}`, data, rawData)
  },
  onItemUpdated: (data: unknown, rawData: unknown) => {
    const fixedItem = fixContactObject(data)

    consola.debug("Received an update for a contact", fixedItem)

    const parsedItem = ContactSchema.safeParse(fixedItem)
    if (!parsedItem.success) {
      consola.error(`We will ignore the update from ${name} because the data is invalid`, parsedItem.error)
      return
    }

    parsedItem.data.Hash = cyrb64Hash(JSON.stringify(parsedItem.data))

    chatStore$.contacts[`${parsedItem.data.ContactPersonId}`].set(parsedItem.data)
  },
  onConnected: () => {
    const syncState$ = syncState(dynamoStore$)
    when(syncState$.isPersistLoaded).then(() => {
      // TODO: Remove this, we should fetch when we receive the list from dynamo
      const currentCachedContacts = dynamoStore$.recentUserContacts.sorted.get()

      batch(() => {
        for (const contact of currentCachedContacts) {
          // Fetch the contact from the map
          const contactUserID = contact.ContactPersonId

          fetchMapKeyData({
            key: contactUserID,
            map: "contacts",
          })
        }
      })
    })
  },
})
